import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";

export const Route = createFileRoute("/palautus")({
  head: () => ({
    meta: [
      { title: "Palautukset ja peruuttamisoikeus | Montessori24" },
      { name: "description", content: "Lakisääteinen 14 päivän peruuttamisoikeus ja Montessori24:n vapaaehtoinen 30 päivän palautusetu — selkeästi eroteltuna. Ilmainen palautus Suomessa." },
      { property: "og:title", content: "Palautukset ja peruuttamisoikeus" },
      { property: "og:description", content: "Lakisääteinen 14 päivän peruuttamisoikeus ja vapaaehtoinen 30 päivän palautusetu." },
      { property: "og:url", content: "https://montessori24.fi/palautus" },
    ],
    links: [{ rel: "canonical", href: "https://montessori24.fi/palautus" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Mikä on palautusaika?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Verkkokaupasta ostettaessa sinulla on kuluttajansuojalain mukainen 14 päivän peruuttamisoikeus. Montessori24 pidentää palautusajan vapaaehtoisesti 30 päivään tuotteen vastaanottamisesta. Vapaaehtoinen lisäaika ei rajoita lakisääteisiä oikeuksiasi.",
            },
          },
          {
            "@type": "Question",
            name: "Onko palautus minulle ilmainen?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Kyllä. Tarjoamme ilmaisen palautuksen koko Suomeen. Lähetämme sähköpostitse valmiin palautusosoitelapun.",
            },
          },
          {
            "@type": "Question",
            name: "Missä kunnossa tuotteen tulee olla?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Lakisääteisen 14 päivän peruuttamisoikeuden aikana saat tutustua tuotteeseen kuten myymälässä. Voit avata pakkauksen ja kokeilla tuotetta; vastaat vain arvonalennuksesta, joka johtuu tätä laajemmasta käytöstä. Vapaaehtoisena 15.–30. päivänä palautettavan tuotteen tulee olla käyttämätön ja myyntikuntoinen, kaikki osat mukana ja alkuperäispakkaus mahdollisuuksien mukaan tallella.",
            },
          },
          {
            "@type": "Question",
            name: "Voinko vaihtaa tuotteen toiseen?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Kyllä. Tee palautus ja tilaa haluamasi tuote uudelleen — näin saat sen nopeimmin. Jos haluat suoraa vaihtoa, ota yhteyttä.",
            },
          },
          {
            "@type": "Question",
            name: "Mitä jos tuote on viallinen?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Otamme aina vastuun viallisista tuotteista. Lähetä kuva viasta sähköpostitse, niin korvaamme tuotteen tai hyvitämme sen kuluitta.",
            },
          },
          {
            "@type": "Question",
            name: "Miten saan rahani takaisin?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Hyvitys palautetaan aina samalle maksutavalle, jolla tilaus on maksettu. Pankkikorttimaksuissa hyvitys näkyy 3–5 arkipäivässä.",
            },
          },
        ],
      }),
    }],
  }),
  component: Returns,
});

const steps = [
  ["1. Ilmoita palautuksesta", "Lähetä viesti osoitteeseen asiakaspalvelu@montessori24.fi ja mainitse tilausnumero. Saat palautusohjeet ja osoitelapun 1 arkipäivän kuluessa."],
  ["2. Pakkaa tuote", "Pakkaa tuote huolellisesti, mieluiten alkuperäispakkaukseen, ja liitä mukaan tilausnumero tai saatekirje."],
  ["3. Lähetä paketti", "Vie paketti lähimpään Postin pisteeseen palautuskoodilla. Palautus on sinulle ilmainen Suomen sisällä."],
  ["4. Saat hyvityksen", "Hyvitämme tuotteen hinnan alkuperäiselle maksutavalle 3–5 arkipäivän kuluessa palautuksen saapumisesta."],
] as const;

const faqs = [
  ["Mikä on palautusaika?", "Sinulla on kuluttajansuojalain mukainen 14 päivän peruuttamisoikeus. Lisäksi Montessori24 pidentää palautusajan vapaaehtoisesti 30 päivään — lisäetu ei rajoita lakisääteisiä oikeuksiasi."],
  ["Onko palautus minulle ilmainen?", "Kyllä. Tarjoamme ilmaisen palautuksen koko Suomeen. Lähetämme sähköpostitse valmiin palautusosoitelapun."],
  ["Missä kunnossa tuotteen tulee olla?", "14 päivän peruuttamisoikeuden aikana saat tutustua tuotteeseen kuten myymälässä: pakkauksen saa avata ja tuotetta kokeilla. 15.–30. päivänä palautettavan tuotteen tulee olla käyttämätön ja myyntikuntoinen."],
  ["Voinko vaihtaa tuotteen toiseen?", "Kyllä. Tee palautus ja tilaa haluamasi tuote uudelleen — näin saat sen nopeimmin. Jos haluat suoraa vaihtoa, ota yhteyttä."],
  ["Mitä jos tuote on viallinen?", "Otamme aina vastuun viallisista tuotteista. Lähetä kuva viasta sähköpostitse, niin korvaamme tuotteen tai hyvitämme sen kuluitta."],
  ["Miten saan rahani takaisin?", "Hyvitys palautetaan aina samalle maksutavalle, jolla tilaus on maksettu. Pankkikorttimaksuissa hyvitys näkyy 3–5 arkipäivässä."],
] as const;

function Returns() {
  return (
    <Layout>
      <section className="container-prose pt-16 pb-12 md:pt-24">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Asiakaspalvelu</span>
            <h1 className="mt-3 text-5xl text-foreground md:text-6xl text-balance">
              Palautukset ja peruuttamisoikeus
            </h1>
            <p className="mt-6 text-muted-foreground">
              Lakisääteinen 14 päivän peruuttamisoikeus ja sen päälle Montessori24:n vapaaehtoinen
              30 päivän palautusetu. Palautus on ilmainen koko Suomeen.
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <ol className="space-y-6">
              {steps.map(([title, desc]) => (
                <li key={title} className="border-b border-border pb-6 last:border-0">
                  <div className="text-base font-medium text-foreground">{title}</div>
                  <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* 14 vs 30 — statutory right kept visually separate from the voluntary benefit */}
      <section className="container-prose pb-16">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-border bg-card p-7">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-clay">Lakisääteinen</p>
            <h2 className="mt-3 text-2xl text-foreground">14 päivän peruuttamisoikeus</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Verkkokaupasta ostettaessa kuluttajalla on pääsääntöisesti 14 päivän peruuttamisoikeus
              tuotteen vastaanottamisesta, laissa säädetyin poikkeuksin. Saat tutustua tuotteeseen
              samalla tavalla kuin myymälässä: pakkauksen saa avata ja tuotetta kokeilla. Vastaat
              vain arvonalennuksesta, joka johtuu tätä laajemmasta käytöstä.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Peruuttamisilmoituksen voi tehdä vapaamuotoisesti sähköpostitse.
            </p>
          </div>
          <div className="rounded-3xl border border-clay/40 bg-cream/60 p-7">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-clay">Vapaaehtoinen lisäetu</p>
            <h2 className="mt-3 text-2xl text-foreground">Montessori24:n 30 päivän palautusetu</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Pidennämme palautusajan vapaaehtoisesti 30 päivään. Päivien 15–30 aikana palautukseen
              sovelletaan seuraavia kohtuullisia ehtoja:
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>tuote on käyttämätön</li>
              <li>tuote on myyntikuntoinen</li>
              <li>alkuperäispakkaus on tallella, mikäli se on kohtuudella mahdollista</li>
              <li>kaikki osat ja tarvikkeet ovat mukana</li>
            </ul>
            <p className="mt-4 rounded-xl bg-background/70 p-4 text-xs leading-relaxed text-foreground">
              30 päivän palautusetu on lisäetu, joka ei rajoita eikä vähennä lakisääteisiä
              kuluttajan oikeuksiasi.
            </p>
          </div>
        </div>
      </section>

      {/* Consumer dispute resolution — Finnish authorities only */}
      <section className="container-prose pb-16">
        <div className="rounded-3xl border border-border bg-background p-7">
          <h2 className="text-2xl text-foreground">Kuluttajaneuvonta ja riidanratkaisu</h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Jos erimielisyyttä ei saada ratkaistua neuvottelemalla, kuluttaja voi olla yhteydessä
            kuluttajaneuvontaan. Kuluttaja voi tarvittaessa saattaa asian kuluttajariitalautakunnan
            käsiteltäväksi.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              className="inline-flex items-center rounded-full border border-border px-4 py-2 text-xs font-medium text-foreground transition hover:border-foreground"
              href="https://www.kkv.fi/kuluttaja-asiat/kuluttajaneuvonta/"
              target="_blank"
              rel="noreferrer"
            >
              Kilpailu- ja kuluttajavirasto — kuluttajaneuvonta
            </a>
            <a
              className="inline-flex items-center rounded-full border border-border px-4 py-2 text-xs font-medium text-foreground transition hover:border-foreground"
              href="https://www.kuluttajariita.fi/"
              target="_blank"
              rel="noreferrer"
            >
              Kuluttajariitalautakunta
            </a>
          </div>
        </div>
      </section>

      <section className="container-prose pb-24">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <h2 className="text-3xl text-foreground md:text-4xl">Usein kysyttyä palautuksista</h2>
            <p className="mt-4 text-sm text-muted-foreground">
              Katso myös <Link to="/toimitus" className="text-foreground underline-offset-4 hover:underline">toimitustiedot</Link> tai
              kaikki vastaukset <Link to="/ukk" className="text-foreground underline-offset-4 hover:underline">UKK-sivulla</Link>.
            </p>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <dl className="divide-y divide-border border-y border-border">
              {faqs.map(([q, a]) => (
                <details key={q} className="group py-6">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-6 text-base text-foreground">
                    <span>{q}</span>
                    <span className="mt-1 text-xl text-muted-foreground transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 max-w-prose text-sm leading-relaxed text-muted-foreground">{a}</p>
                </details>
              ))}
            </dl>
          </div>
        </div>
      </section>
    </Layout>
  );
}
